import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronRight, User, Bot, Loader2 } from 'lucide-react';

interface ToolCall {
  name: string;
  input: Record<string, any>;
  result: any;
}

interface UsageData {
  input_tokens: number;
  output_tokens: number;
}

interface MessageData {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCall[];
  timestamp: string;
  usage?: UsageData;
  model?: string;
}

interface AssistantMessageProps {
  message: MessageData;
  isLoading?: boolean;
}

// Friendly tool names mapping — must match mcp-server/src/tools/
const toolNameMap: Record<string, string> = {
  dashboard_statistiche: 'Statistiche dashboard',
  cerca_associati: 'Ricerca associati',
  dettaglio_associato: 'Dettaglio associato',
  report_associati: 'Report associati',
  aggiorna_associato: 'Aggiornamento associato',
  lista_articoli: 'Lista articoli',
  crea_articolo: 'Creazione articolo',
  lista_eventi: 'Lista eventi',
  crea_evento: 'Creazione evento',
  lista_sondaggi: 'Lista sondaggi',
  risultati_sondaggio: 'Risultati sondaggio',
  crea_sondaggio: 'Creazione sondaggio',
  statistiche_email: 'Statistiche email',
  crea_campagna_email: 'Creazione campagna email',
  statistiche_pagamenti: 'Statistiche pagamenti',
  lista_pagamenti: 'Lista pagamenti',
  lista_feedback: 'Lista feedback',
  dettaglio_feedback: 'Dettaglio feedback',
  rispondi_feedback: 'Risposta feedback',
  aggiorna_stato_feedback: 'Aggiornamento stato feedback',
  stato_rete_certificata: 'Stato rete certificata',
  statistiche_push: 'Statistiche push',
  invia_notifica_push: 'Invio notifica push',
};

// Cost per million tokens (USD) — stesso listino di backend/utils/assistantModelli.js (PREZZI)
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  'claude-sonnet-5-5': { input: 2, output: 10 },
  'claude-haiku-5-5': { input: 0.1, output: 0.5 },
  'claude-opus-5-5': { input: 4, output: 20 },
  'claude-sonnet-4-20250514': { input: 3, output: 15 },
  'claude-haiku-4-5-20251001': { input: 1, output: 5 },
};
// I messaggi salvati senza modello sono di Sonnet 4, il predefinito di allora
const DEFAULT_PRICING = { input: 3, output: 15 };

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatModelLabel(model?: string): string {
  if (!model) return '';
  const versione = /-5-5$/.test(model) ? ' 5.5' : '';
  if (model.includes('haiku')) return `Haiku${versione}`;
  if (model.includes('sonnet')) return `Sonnet${versione}`;
  if (model.includes('opus')) return `Opus${versione}`;
  return model;
}

function formatUsage(usage: UsageData, model?: string): string {
  const pricing = (model && MODEL_PRICING[model]) || DEFAULT_PRICING;
  const costUsd = (usage.input_tokens * pricing.input + usage.output_tokens * pricing.output) / 1_000_000;
  const totalTokens = usage.input_tokens + usage.output_tokens;
  const modelLabel = formatModelLabel(model);
  return `${modelLabel ? modelLabel + ' · ' : ''}${totalTokens.toLocaleString('it-IT')} token · ~$${costUsd.toFixed(4)}`;
}

function ToolCallDetail({ toolCall }: { toolCall: ToolCall }) {
  const [open, setOpen] = useState(false);
  const friendlyName = toolNameMap[toolCall.name] || toolCall.name;

  return (
    <div className="mt-2 border border-gray-200 dark:border-gray-600 rounded text-xs">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-left text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
        <span className="font-medium">{friendlyName}</span>
      </button>
      {open && (
        <div className="px-2.5 py-2 border-t border-gray-200 dark:border-gray-600 space-y-2">
          <div>
            <span className="text-gray-500 dark:text-gray-400 font-medium">Parametri:</span>
            <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded overflow-x-auto text-[11px] leading-relaxed">
              {JSON.stringify(toolCall.input, null, 2)}
            </pre>
          </div>
          {toolCall.result && (
            <div>
              <span className="text-gray-500 dark:text-gray-400 font-medium">Risultato:</span>
              <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded overflow-x-auto text-[11px] leading-relaxed max-h-48 overflow-y-auto">
                {typeof toolCall.result === 'string' ? toolCall.result : JSON.stringify(toolCall.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex items-start gap-2.5 mb-3">
      <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </div>
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
        <div className="flex items-center gap-1">
          <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
          <span className="text-xs text-gray-500 dark:text-gray-400">Sta pensando</span>
          <span className="flex gap-0.5">
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
        </div>
      </div>
    </div>
  );
}

const AssistantMessage: React.FC<AssistantMessageProps> = ({ message, isLoading }) => {
  const isUser = message.role === 'user';

  if (isLoading) {
    return <ThinkingIndicator />;
  }

  return (
    <div className={`flex items-start gap-2.5 mb-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser
          ? 'bg-indigo-600 text-white'
          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      {/* Message bubble */}
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`rounded-lg px-3 py-2 ${
          isUser
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        }`}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="text-sm max-w-none assistant-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-2 -mx-1">
                      <table className="min-w-full text-xs border-collapse border border-gray-300 dark:border-gray-600">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-gray-200 dark:bg-gray-600">
                      {children}
                    </thead>
                  ),
                  th: ({ children }) => (
                    <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 text-xs">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="px-2 py-1 border border-gray-300 dark:border-gray-600 text-xs">
                      {children}
                    </td>
                  ),
                  tr: ({ children }) => (
                    <tr className="even:bg-gray-50 dark:even:bg-gray-750">
                      {children}
                    </tr>
                  ),
                  p: ({ children }) => (
                    <p className="mb-1.5 last:mb-0 leading-relaxed text-sm">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="leading-relaxed text-sm">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  code: ({ children, className }) => {
                    const isBlock = className?.includes('language-');
                    if (isBlock) {
                      return (
                        <code className="block bg-gray-800 text-gray-100 rounded p-2 my-1.5 text-xs overflow-x-auto">
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code className="bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-xs">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="my-1.5 overflow-x-auto">{children}</pre>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-sm font-bold mb-1.5 mt-2">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-sm font-bold mb-1 mt-2">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-sm font-semibold mb-1 mt-1.5">{children}</h3>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline hover:no-underline">
                      {children}
                    </a>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-3 border-gray-300 dark:border-gray-500 pl-2 my-1.5 italic text-gray-600 dark:text-gray-400">
                      {children}
                    </blockquote>
                  ),
                  hr: () => (
                    <hr className="my-2 border-gray-300 dark:border-gray-600" />
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Tool calls (assistant only) */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-1">
            {message.toolCalls.map((tc, i) => (
              <ToolCallDetail key={i} toolCall={tc} />
            ))}
          </div>
        )}

        {/* Timestamp + Usage */}
        <div className={`mt-0.5 text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5 ${isUser ? 'justify-end' : ''}`}>
          <span>{formatTimestamp(message.timestamp)}</span>
          {!isUser && message.usage && (message.usage.input_tokens > 0 || message.usage.output_tokens > 0) && (
            <span className="text-gray-300 dark:text-gray-600" title={`Input: ${message.usage.input_tokens.toLocaleString('it-IT')} · Output: ${message.usage.output_tokens.toLocaleString('it-IT')}`}>
              {formatUsage(message.usage, message.model)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssistantMessage;
